/** What the account, orders and risk views share to offer commands: the gate, the launchers, the dialog (U043-U050). */
import type { BalanceRow } from "../contracts/account.ts";
import type { ReadEnvelope } from "../contracts/envelope.ts";
import { openCommandDialog, type DialogHandle } from "../app/command-dialog-view.ts";
import {
  INTENTS,
  controlName,
  setCommandEnabled,
  type CommandIntent,
  type RoutingGuard,
} from "../app/command-dialog.ts";
import { h } from "../app/dom.ts";
import { anchor, isAged, type ServerClock } from "../app/time.ts";
import type { ViewContext } from "../app/view.ts";
import type { Environment, Mode } from "../contracts/envelope.ts";

interface Gate {
  readonly clock: ServerClock;
  readonly ageUs: number | null;
  readonly boundUs: number;
  readonly actionsEnabled: boolean;
}

interface Identity {
  readonly environment: Environment;
  readonly mode: Mode;
  readonly accountVersion: number;
}

/** One control to offer: which intent, on what, and whether the data allows it now. */
export interface LauncherSpec {
  readonly intent: CommandIntent;
  readonly scope: string;
  readonly quantities: string;
  readonly payload: Readonly<Record<string, string>>;
  readonly maxQuantity?: number;
  /** The guards the server's routing facts put on the dialog (cooldown, review, code, cap, floor). */
  readonly routing?: RoutingGuard;
  /** Whether the data's state makes this action sensible now (an enabled control to pause, a paused one to resume). */
  readonly applicable?: boolean;
  /** Said beside the control when it is not applicable. */
  readonly inapplicable?: string | undefined;
}

/** The commands a view can offer, and the one rule for when they may be used (UX01, UX05). */
export class CommandSurface {
  /** A live region stating whether commands are enabled and, if not, why. */
  readonly status: HTMLElement = h("p", {
    class: "command-status",
    attrs: { "aria-live": "polite", "data-command-status": "", tabindex: "-1" },
  });
  private readonly ctx: ViewContext;
  private readonly refresh: () => Promise<void>;
  private readonly gates = new Map<string, Gate>();
  private readonly launchers: {
    readonly button: HTMLButtonElement;
    readonly applicable: boolean;
  }[] = [];
  private identity: Identity | null = null;
  private dialog: DialogHandle | null = null;

  constructor(ctx: ViewContext, refresh: () => Promise<void>) {
    this.ctx = ctx;
    this.refresh = refresh;
    ctx.scope.every(1000, () => {
      this.sync();
    });
    ctx.scope.add(
      ctx.commandsEnabled.subscribe(() => {
        this.sync();
      }),
    );
    ctx.scope.add(() => {
      this.dialog?.close();
    });
    this.sync();
  }

  /** Whether a P0-E intent exists at all: broker routing must be enabled (ruling 0.14). */
  offered(intent: CommandIntent): boolean {
    return !INTENTS[intent].routed || this.ctx.capabilities.routingEnabled;
  }

  /** Record the freshness of a read this view's commands depend on. */
  observe(name: string, envelope: ReadEnvelope<unknown>): void {
    this.gates.set(name, {
      clock: anchor(envelope.snapshotTime, this.ctx.mono),
      ageUs: envelope.ageUs,
      boundUs: envelope.freshnessBoundUs,
      actionsEnabled: envelope.actionsEnabled,
    });
    this.sync();
  }

  /** Take the account state version and context from the balances read; without a mode, no command (fail closed). */
  identifyFrom(balances: ReadEnvelope<BalanceRow>): void {
    const [account] = balances.page.rows;
    if (account !== undefined && balances.mode !== null) {
      this.identity = {
        environment: balances.environment,
        mode: balances.mode,
        accountVersion: account.accountVersion,
      };
    }
    this.sync();
  }

  /** Drop the launchers of a view that is about to redraw them. */
  clearLaunchers(): void {
    this.launchers.length = 0;
  }

  /** Forget everything observed (a re-read is under way): no command until it lands. */
  reset(): void {
    this.gates.clear();
    this.identity = null;
    this.sync();
  }

  /** Why commands are off now, or `null` when they are on. */
  reason(): string | null {
    if (!this.ctx.commandsEnabled.get()) {
      return "the live stream is not connected and fresh, or the browser is unsupported";
    }
    if (this.identity === null || this.gates.size === 0) {
      return "this view has not loaded the account state yet";
    }
    for (const gate of this.gates.values()) {
      if (!gate.actionsEnabled || isAged(gate.ageUs, gate.boundUs, gate.clock, this.ctx.mono)) {
        return "this view's data is stale or superseded";
      }
    }
    return null;
  }

  /** Re-derive every control's enabled state and the status line, in place. */
  sync(): void {
    const reason = this.reason();
    this.status.textContent =
      reason === null
        ? "Commands are enabled. Opening one asks for your confirmation; nothing is sent until you confirm."
        : `Commands are disabled: ${reason}.`;
    this.status.dataset.commandsOpen = String(reason === null);
    for (const { button, applicable } of this.launchers) {
      const wasFocused = button.ownerDocument.activeElement === button;
      setCommandEnabled(button, reason === null && applicable);
      if (wasFocused && button.hasAttribute("disabled")) {
        this.status.focus();
      }
    }
  }

  /** A control that opens the dialog of an intent; it sends nothing itself (U100, V08-A06). */
  launcher(spec: LauncherSpec): HTMLElement {
    const applicable = spec.applicable ?? true;
    const accountId = String(this.ctx.accountId);
    const button = h(
      "button",
      {
        class: "command-control",
        attrs: {
          type: "button",
          "data-command-control": spec.intent,
          "aria-label": controlName(spec.intent, accountId, spec.scope),
        },
        on: {
          click: () => {
            this.open(spec, button);
          },
        },
      },
      INTENTS[spec.intent].label,
    );
    this.launchers.push({ button, applicable });
    this.sync();
    return applicable || spec.inapplicable === undefined
      ? button
      : h("span", { class: "command-slot" }, button, h("small", {}, ` ${spec.inapplicable}`));
  }

  private open(spec: LauncherSpec, opener: HTMLElement): void {
    const clock = this.gates.get("balances")?.clock;
    if (this.reason() !== null || this.identity === null || clock === undefined) {
      return;
    }
    this.dialog?.close();
    const dialog = openCommandDialog(
      {
        services: this.ctx.commands,
        mono: this.ctx.mono,
        onConflict: () => {
          void this.refresh();
        },
        onSettled: () => {
          void this.refresh();
        },
        onIssued: () => {
          void this.refresh();
        },
      },
      {
        intent: spec.intent,
        accountId: String(this.ctx.accountId),
        environment: this.identity.environment,
        mode: this.identity.mode,
        scope: spec.scope,
        quantities: spec.quantities,
        payload: spec.payload,
        expectedVersion: this.identity.accountVersion,
        maxQuantity: spec.maxQuantity ?? 0,
        clock,
        ...(spec.routing === undefined ? {} : { routing: spec.routing }),
      },
      opener,
    );
    this.dialog = dialog;
  }
}
